import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import api from '../services/api';

interface Translation {
  id: number;
  message_key: string;
  category: 'event' | 'summary' | 'generic' | 'ui';
  text_fr: string;
  text_comorien?: string;
  text_mahorais?: string;
  audio_path_comorien?: string;
  audio_path_mahorais?: string;
  description?: string;
  context?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  audio_exists?: boolean;
  audio_size?: number;
}

interface TranslationStats {
  by_category: Array<{
    category: string;
    count: number;
    with_audio: number;
  }>;
  total: {
    total: number;
    total_with_audio: number;
    total_translated: number;
  };
}

const TranslationsManagement: React.FC = () => {
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<Translation[]>([]);
  const [stats, setStats] = useState<TranslationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [currentTab, setCurrentTab] = useState(0);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [currentTranslation, setCurrentTranslation] = useState<Partial<Translation>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Audio upload
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioLanguage, setAudioLanguage] = useState('comorien');

  // Audio playback
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadTranslations();
    loadStats();
  }, []);

  useEffect(() => {
    filterTranslations();
  }, [translations, searchTerm, categoryFilter, currentTab]);

  const loadTranslations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/translations');
      if (response.data.success) {
        setTranslations(response.data.translations);
      }
    } catch (err: any) {
      console.error('Erreur chargement traductions:', err);
      setError('Impossible de charger les traductions');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/translations/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const filterTranslations = () => {
    let filtered = [...translations];

    // Filtrer par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    // Filtrer par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        t =>
          t.message_key.toLowerCase().includes(search) ||
          t.text_fr.toLowerCase().includes(search) ||
          t.text_comorien?.toLowerCase().includes(search) ||
          t.description?.toLowerCase().includes(search)
      );
    }

    // Filtrer par onglet (actif/inactif)
    if (currentTab === 1) {
      filtered = filtered.filter(t => !t.is_active);
    } else if (currentTab === 0) {
      filtered = filtered.filter(t => t.is_active);
    }

    setFilteredTranslations(filtered);
  };

  const handleOpenDialog = (mode: 'create' | 'edit', translation?: Translation) => {
    setDialogMode(mode);
    if (mode === 'edit' && translation) {
      setCurrentTranslation(translation);
    } else {
      setCurrentTranslation({
        category: 'event',
        is_active: true
      });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTranslation({});
    setAudioFile(null);
    setError(null);
  };

  const handleSaveTranslation = async () => {
    try {
      setSaving(true);
      setError(null);

      if (dialogMode === 'create') {
        const response = await api.post('/translations', currentTranslation);
        if (response.data.success) {
          setSuccess('Traduction créée avec succès');
          loadTranslations();
          loadStats();
          handleCloseDialog();
        }
      } else {
        const response = await api.put(`/translations/${currentTranslation.id}`, currentTranslation);
        if (response.data.success) {
          setSuccess('Traduction mise à jour avec succès');
          loadTranslations();
          loadStats();
          handleCloseDialog();
        }
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Erreur sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTranslation = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette traduction ?')) {
      return;
    }

    try {
      const response = await api.delete(`/translations/${id}`);
      if (response.data.success) {
        setSuccess('Traduction supprimée avec succès');
        loadTranslations();
        loadStats();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Erreur suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleUploadAudio = async (translationId: number) => {
    if (!audioFile) {
      setError('Veuillez sélectionner un fichier audio');
      return;
    }

    try {
      setUploadingAudio(true);
      setError(null);

      const formData = new FormData();
      formData.append('audio_file', audioFile);
      formData.append('language', audioLanguage);
      formData.append('message_key', translations.find(t => t.id === translationId)?.message_key || '');

      const response = await api.post(`/translations/${translationId}/upload-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess('Fichier audio uploadé avec succès');
        loadTranslations();
        loadStats();
        setAudioFile(null);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Erreur upload audio:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handlePlayAudio = (audioPath: string, translationId: number) => {
    if (playingAudio === translationId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(audioPath);
      audio.onended = () => setPlayingAudio(null);
      audio.play();
      audioRef.current = audio;
      setPlayingAudio(translationId);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      event: 'primary',
      summary: 'secondary',
      generic: 'success',
      ui: 'info'
    };
    return colors[category] || 'default';
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      event: 'Événement',
      summary: 'Résumé',
      generic: 'Générique',
      ui: 'Interface'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={1}>
              <TranslateIcon fontSize="large" />
              Gestion des Traductions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gérez tous les textes et fichiers audio de votre application
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Nouvelle traduction
          </Button>
        </Box>

        {/* Alerts */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total traductions
                  </Typography>
                  <Typography variant="h4">{stats.total.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Avec audio
                  </Typography>
                  <Typography variant="h4">{stats.total.total_with_audio}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.total.total_with_audio / stats.total.total) * 100}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Traduites en comorien
                  </Typography>
                  <Typography variant="h4">{stats.total.total_translated}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.total.total_translated / stats.total.total) * 100}
                    sx={{ mt: 1 }}
                    color="secondary"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Catégories
                  </Typography>
                  <Typography variant="h4">{stats.by_category.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Rechercher par clé, texte, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Catégorie"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">Toutes les catégories</MenuItem>
                  <MenuItem value="event">Événements</MenuItem>
                  <MenuItem value="summary">Résumés</MenuItem>
                  <MenuItem value="generic">Génériques</MenuItem>
                  <MenuItem value="ui">Interface</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}>
            <Tab label={`Actives (${translations.filter(t => t.is_active).length})`} />
            <Tab label={`Inactives (${translations.filter(t => !t.is_active).length})`} />
          </Tabs>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Clé</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Texte français</TableCell>
                <TableCell>Texte comorien</TableCell>
                <TableCell align="center">Audio</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTranslations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      Aucune traduction trouvée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTranslations.map((translation) => (
                  <TableRow key={translation.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {translation.message_key}
                      </Typography>
                      {translation.description && (
                        <Typography variant="caption" color="text.secondary">
                          {translation.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(translation.category)}
                        color={getCategoryColor(translation.category) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap maxWidth={200}>
                        {translation.text_fr}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {translation.text_comorien ? (
                        <Typography variant="body2" noWrap maxWidth={200}>
                          {translation.text_comorien}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Non traduit
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {translation.audio_path_comorien ? (
                        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                          <Tooltip title="Écouter">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePlayAudio(translation.audio_path_comorien!, translation.id)}
                            >
                              {playingAudio === translation.id ? (
                                <VolumeUpIcon />
                              ) : (
                                <PlayIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          {translation.audio_exists ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <CancelIcon fontSize="small" color="error" />
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <input
                            type="file"
                            accept="audio/*"
                            id={`audio-upload-${translation.id}`}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setAudioFile(file);
                                handleUploadAudio(translation.id);
                              }
                            }}
                          />
                          <label htmlFor={`audio-upload-${translation.id}`}>
                            <Tooltip title="Upload audio">
                              <IconButton component="span" size="small" color="default">
                                <UploadIcon />
                              </IconButton>
                            </Tooltip>
                          </label>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={translation.is_active ? 'Actif' : 'Inactif'}
                        color={translation.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('edit', translation)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTranslation(translation.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog Create/Edit */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogMode === 'create' ? 'Nouvelle traduction' : 'Modifier la traduction'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Clé du message"
                    value={currentTranslation.message_key || ''}
                    onChange={(e) =>
                      setCurrentTranslation({ ...currentTranslation, message_key: e.target.value })
                    }
                    disabled={dialogMode === 'edit'}
                    required
                    helperText="Identifiant unique (ex: DEMANDE_COMPLEMENT)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Catégorie</InputLabel>
                    <Select
                      value={currentTranslation.category || 'event'}
                      label="Catégorie"
                      onChange={(e) =>
                        setCurrentTranslation({ ...currentTranslation, category: e.target.value as any })
                      }
                    >
                      <MenuItem value="event">Événement</MenuItem>
                      <MenuItem value="summary">Résumé</MenuItem>
                      <MenuItem value="generic">Générique</MenuItem>
                      <MenuItem value="ui">Interface</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Texte en français"
                    value={currentTranslation.text_fr || ''}
                    onChange={(e) =>
                      setCurrentTranslation({ ...currentTranslation, text_fr: e.target.value })
                    }
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Texte en comorien"
                    value={currentTranslation.text_comorien || ''}
                    onChange={(e) =>
                      setCurrentTranslation({ ...currentTranslation, text_comorien: e.target.value })
                    }
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={currentTranslation.description || ''}
                    onChange={(e) =>
                      setCurrentTranslation({ ...currentTranslation, description: e.target.value })
                    }
                    multiline
                    rows={2}
                    helperText="Expliquez quand ce message est utilisé"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contexte"
                    value={currentTranslation.context || ''}
                    onChange={(e) =>
                      setCurrentTranslation({ ...currentTranslation, context: e.target.value })
                    }
                    multiline
                    rows={2}
                    helperText="Contexte d'utilisation technique"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentTranslation.is_active !== false}
                        onChange={(e) =>
                          setCurrentTranslation({ ...currentTranslation, is_active: e.target.checked })
                        }
                      />
                    }
                    label="Traduction active"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button
              variant="contained"
              onClick={handleSaveTranslation}
              disabled={saving || !currentTranslation.message_key || !currentTranslation.text_fr}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default TranslationsManagement;
